import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PackageSearch, Loader2, CheckCircle2, Circle, MessageCircle, Phone, LifeBuoy, Package, PackageCheck, Truck, MapPin, AlertTriangle, RotateCcw, LayoutDashboard, Check, ArrowRight, Share2 } from "lucide-react";
import { motion } from "framer-motion";
import StatusBadge from "../components/StatusBadge";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { MarketingHero, GradientCta } from "../components/marketing/Blocks";
import api, { fmtErr, fmtDateTime, fmtDate } from "../lib/api";
import { toast } from "sonner";

export default function TrackPage({ embedded = false }) {
  const { awb: paramAwb } = useParams();
  const navigate = useNavigate();
  const [awb, setAwb] = useState(paramAwb || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const track = async (code) => {
    if (!code?.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/track/${code.trim().toUpperCase()}`);
      setData(data);
    } catch (e) {
      setData(null);
      toast.error(fmtErr(e, "Tracking lookup failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (paramAwb) track(paramAwb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramAwb]);

  const submit = (e) => {
    e.preventDefault();
    if (embedded) { track(awb); return; }
    navigate(`/track/${awb.trim().toUpperCase()}`);
  };

  const doneCount = data ? data.events.filter((e) => e.completed).length : 0;
  // Status floor keeps the route truthful for sparse event feeds (e.g. a single IN_TRANSIT
  // scan would otherwise render as 100% of the journey).
  const eventPct = data ? Math.round((doneCount / Math.max(data.events.length, 1)) * 100) : 0;
  // The route position is status-driven: the events list is a milestone template, so a single
  // IN_TRANSIT scan would otherwise compute 100%. Unknown statuses fall back to event progress.
  const STATUS_FLOOR = { BOOKED: 8, PICKUP_SCHEDULED: 18, PICKED_UP: 32, IN_TRANSIT: 55, OUT_FOR_DELIVERY: 82, DELIVERED: 100, CANCELLED: 0, NDR: 62, RTO: 25 };
  const pct = !data ? 0 : (STATUS_FLOOR[data.status] ?? Math.min(eventPct, 95));

  // Share a WhatsApp-friendly tracking message — native share sheet on mobile (WhatsApp,
  // SMS…), clipboard copy + toast on desktop. Link previews render via the site OG tags.
  const shareTracking = async () => {
    if (!data) return;
    const url = `${window.location.origin}/track/${data.awb}`;
    const journey = data.status === "DELIVERED" ? "journey complete" : `${pct}% of the journey`;
    const text = `Track my shipment ${data.awb} — ${data.status.replace(/_/g, " ")} (${journey}). Live tracking: ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: `Shipment tracking — ${data.awb}`, text, url }); } catch { /* user dismissed */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Tracking link copied — paste it in WhatsApp!");
    } catch {
      toast.error("Copy failed — long-press the link instead");
    }
  };

  // White-label theming: brand kicks in when the shipment owner set a name or logo.
  const brand = data?.brand;
  const isBranded = !!brand && ((brand.brand_name && brand.brand_name !== "SHIPANY") || !!brand.logo_url);
  const accent = isBranded && brand.primary_color ? brand.primary_color : null;
  const waPhone = (brand?.support_phone || "").replace(/\D/g, "") || "919810012345";
  const telPhone = brand?.support_phone || "+919810012345";

  const supportCls = "flex flex-col items-center gap-1.5 rounded-2xl border border-white/10 bg-shipany-panel p-4 transition-colors hover:border-shipany-blue/60";

  const header = (
      <header className="glass-navy sticky top-0 z-40 border-b border-shipany-cyan/15" style={accent ? { borderColor: `${accent}40` } : undefined}>
        <div className="mx-auto flex max-w-2xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center" data-testid="track-brand">
            {isBranded ? (
              brand.logo_url
                ? <span className="rounded-lg bg-white px-2.5 py-1"><img src={brand.logo_url} alt={brand.brand_name || "Brand"} className="h-6 w-auto object-contain" /></span>
                : <span className="font-display text-lg font-extrabold text-white">{brand.brand_name}</span>
            ) : (
              <span className="rounded-lg bg-white px-2.5 py-1"><img src="/logo.png" alt="ShipAny" className="h-6 w-auto object-contain" /></span>
            )}
          </Link>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em]" style={accent ? { color: accent } : undefined} data-testid="track-live-label">
            <span className={accent ? "" : "text-slate-400"}>Live Tracking</span>
          </p>
        </div>
      </header>
  );

  const body = (
      <main className="mx-auto max-w-2xl px-5 pb-24 pt-10">
        {!embedded && (
          <>
            <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Real-Time Shipment Tracking, Made Simple</h1>
            <p className="mt-2 text-sm text-slate-400">Track your {isBranded && brand.brand_name ? brand.brand_name : "ShipAny"} shipment from pickup to final delivery — every step, in one tracking experience.</p>
          </>
        )}

        <form onSubmit={submit} className="mt-6 flex gap-2.5" data-testid="public-track-form">
          <input data-testid="public-track-input" value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="e.g. SRK2067371093 or SH1000000000"
            className="mono flex-1 rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white placeholder:text-slate-500 focus:outline-none"
            style={accent ? { borderColor: `${accent}59` } : undefined} />
          <button data-testid="public-track-submit-btn" disabled={loading}
            className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-3.5 text-sm font-bold transition-[filter] hover:brightness-110 disabled:opacity-60 glow-blue"
            style={accent ? { background: accent } : undefined}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageSearch className="h-4 w-4" />}
            <span className="hidden sm:inline">Track</span>
          </button>
        </form>

        {data && (
          <div className="mt-8 space-y-5" data-testid="track-result">
            {isBranded && (
              <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-shipany-panel p-4" data-testid="track-white-label" style={accent ? { borderColor: `${accent}33` } : undefined}>
                {brand.logo_url
                  ? <img src={brand.logo_url} alt={brand.brand_name || "Brand"} className="h-9 w-9 rounded-lg object-cover" />
                  : <span className="flex h-9 w-9 items-center justify-center rounded-lg font-display text-sm font-extrabold text-white" style={{ background: brand.primary_color || "#0757D9" }}>{(brand.brand_name || "B")[0]}</span>}
                <div>
                  <p className="font-display text-sm font-bold">{brand.brand_name}</p>
                  <p className="text-[11px] text-slate-400">Shipment support: {brand.support_email || "support"}{brand.support_phone ? ` · ${brand.support_phone}` : ""}</p>
                </div>
                <span className="ml-auto text-[10px] text-slate-500">Powered by SHIPANY</span>
              </div>
            )}
            {/* Status hero card */}
            <div className="rounded-3xl border border-shipany-cyan/20 bg-shipany-panel p-6" style={accent ? { borderColor: `${accent}40` } : undefined}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="mono text-lg font-bold" data-testid="track-awb">{data.awb}</p>
                  <p className="mt-1 text-xs text-slate-400">Order {data.order_number} · {data.courier_name}</p>
                </div>
                <StatusBadge status={data.status} />
              </div>
              <div className="mt-5" data-testid="track-route">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold">{data.origin_city}</span>
                  <span className="font-semibold">{data.destination_city}</span>
                </div>
                <style>{`
                  @keyframes courier-drive { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
                  @keyframes courier-wheel { to { transform: rotate(360deg); } }
                `}</style>
                <div className="relative mt-3 h-11">
                  <span className="absolute left-0 right-0 top-1/2 h-0.5 -translate-y-1/2 rounded bg-white/10" />
                  <span className="absolute left-0 top-1/2 h-0.5 -translate-y-1/2 rounded bg-shipany-cyan transition-[width] duration-700" style={{ width: `${pct}%`, ...(accent ? { background: accent } : {}) }} />
                  <span className="absolute left-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-shipany-cyan" style={accent ? { background: accent } : undefined} />
                  <span className={`absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full ${data.status === "DELIVERED" ? "bg-emerald-400" : "bg-white/20"}`} />
                  {/* animated courier truck riding the route at the journey progress point */}
                  <div className="absolute top-1/2 transition-[left] duration-700" style={{ left: `calc(${Math.min(Math.max(pct, 7), 90)}% - 26px)`, transform: "translateY(-50%)" }}
                    data-testid="track-route-truck">
                    <div className="relative flex items-end" style={{ animation: "courier-drive 0.6s ease-in-out infinite" }}>
                      <div className="relative flex h-6 w-10 items-center justify-center rounded-[4px] bg-shipany-blue shadow-md">
                        <span className="font-display text-[6px] font-black italic text-white">ShipAny</span>
                        <div className="absolute -top-1.5 right-0.5 h-2 w-2 rounded-[2px] bg-amber-400" />
                      </div>
                      <div className="relative ml-0.5 h-4 w-3.5 rounded-tr-[3px] bg-shipany-cyan shadow-md">
                        <div className="absolute left-0.5 top-0.5 h-1.5 w-1.5 rounded-[2px] bg-white/80" />
                      </div>
                      <div className="absolute -bottom-1 left-1 h-2 w-2 rounded-full border-2 border-slate-600 bg-white" style={{ animation: "courier-wheel 0.5s linear infinite" }} />
                      <div className="absolute -bottom-1 right-0.5 h-2 w-2 rounded-full border-2 border-slate-600 bg-white" style={{ animation: "courier-wheel 0.5s linear infinite" }} />
                    </div>
                  </div>
                </div>
                <p className="mt-1.5 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500" data-testid="track-route-pct">
                  {data.status === "DELIVERED" ? "Delivered — journey complete" : data.status === "CANCELLED" ? "Shipment cancelled" : `${pct}% of the journey`}
                </p>
              </div>
              {data.tracking_note && (
                <div className="mt-4 rounded-xl border border-amber-300/30 bg-amber-400/10 px-4 py-3 text-xs font-medium text-amber-100" data-testid="tracking-stale-note">
                  {data.tracking_note}
                </div>
              )}
              <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl bg-white/5 p-3"><p className="text-[10px] uppercase tracking-widest text-slate-400">Weight</p><p className="mono mt-1 text-sm font-bold">{data.weight} kg</p></div>
                <div className="rounded-xl bg-white/5 p-3"><p className="text-[10px] uppercase tracking-widest text-slate-400">Mode</p><p className="mono mt-1 text-sm font-bold">{data.payment_mode}</p></div>
                <div className="rounded-xl bg-white/5 p-3"><p className="text-[10px] uppercase tracking-widest text-slate-400">ETA</p><p className="mono mt-1 text-sm font-bold text-shipany-cyan" style={accent ? { color: accent } : undefined}>{fmtDate(data.eta_date)}</p></div>
              </div>
              {data.receiver && (data.receiver.name || data.receiver.city) && (
                <div className="mt-3 rounded-xl bg-white/5 p-3" data-testid="track-receiver">
                  <p className="text-[10px] uppercase tracking-widest text-slate-400">Receiver</p>
                  <p className="mt-1 text-sm font-bold" data-testid="track-receiver-name">{data.receiver.name}</p>
                  <p className="text-xs text-slate-400">{[data.receiver.city, data.receiver.state, data.receiver.pincode].filter(Boolean).join(", ")}</p>
                  {data.receiver.phone && <p className="mono mt-0.5 text-xs text-slate-400" data-testid="track-receiver-phone">{data.receiver.phone}</p>}
                </div>
              )}
            </div>

            {/* Timeline */}
            {data.forwarding_leg && (
              <div className="rounded-3xl border border-white/10 bg-shipany-panel p-6" data-testid="track-domestic-leg">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-display text-sm font-bold uppercase tracking-widest text-slate-400">Leg 1 · Domestic transfer to international hub</h2>
                  <StatusBadge status={data.forwarding_leg.status} />
                </div>
                <p className="mono mt-1 text-[11px] text-slate-500">AWB {data.forwarding_leg.awb}{data.forwarding_leg.courier_name ? ` · ${data.forwarding_leg.courier_name}` : ""}</p>
                <div className="mt-4">
                  {data.forwarding_leg.events.map((e, i) => (
                    <div key={i} className="relative flex gap-4 pb-4 last:pb-0">
                      {i < data.forwarding_leg.events.length - 1 && <span className="absolute left-[10px] top-6 h-full w-0.5 bg-white/10" />}
                      {e.completed
                        ? <CheckCircle2 className="relative z-10 h-[22px] w-[22px] shrink-0 text-emerald-400" />
                        : <Circle className="relative z-10 h-[22px] w-[22px] shrink-0 text-slate-600" />}
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className={`text-sm font-semibold ${e.completed ? "text-white" : "text-slate-500"}`}>{e.note || e.status.replace(/_/g, " ")}</p>
                          <p className="mono text-[11px] text-slate-500">{fmtDateTime(e.timestamp)}</p>
                        </div>
                        <p className="mt-0.5 text-xs text-slate-400">{e.location || ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-3xl border border-white/10 bg-shipany-panel p-6" data-testid="track-timeline">
              <h2 className="font-display text-sm font-bold uppercase tracking-widest text-slate-400">{data.forwarding_leg ? "Leg 2 · International journey" : "Journey"}</h2>
              <div className="mt-5">
                {data.events.map((e, i) => (
                  <div key={i} className="relative flex gap-4 pb-6 last:pb-0">
                    {i < data.events.length - 1 && <span className="absolute left-[10px] top-6 h-full w-0.5 bg-white/10" />}
                    {e.completed
                      ? <CheckCircle2 className={`relative z-10 h-[22px] w-[22px] shrink-0 ${i === doneCount - 1 && data.status !== "DELIVERED" ? "text-shipany-cyan pulse-dot" : "text-emerald-400"}`}
                          style={accent && i === doneCount - 1 && data.status !== "DELIVERED" ? { color: accent } : undefined} />
                      : <Circle className="relative z-10 h-[22px] w-[22px] shrink-0 text-slate-600" />}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className={`text-sm font-semibold ${e.completed ? "text-white" : "text-slate-500"}`}>{e.note || e.status.replace(/_/g, " ")}</p>
                        <p className="mono text-[11px] text-slate-500">{fmtDateTime(e.timestamp)}</p>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-400">{e.location || ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Support strip — customer's own contact channels, SHIPANY fallback */}
            <div className="grid grid-cols-4 gap-3">
              <button onClick={shareTracking} data-testid="track-share-btn"
                className="flex flex-col items-center gap-1.5 rounded-2xl border border-white/10 bg-shipany-panel p-4 transition-colors hover:border-shipany-cyan/50">
                <Share2 className="h-5 w-5 text-shipany-cyan" style={accent ? { color: accent } : undefined} /><span className="text-[11px] font-semibold">Share</span>
              </button>
              <a href={`https://wa.me/${waPhone}`} target="_blank" rel="noreferrer" data-testid="track-whatsapp-btn"
                className="flex flex-col items-center gap-1.5 rounded-2xl border border-white/10 bg-shipany-panel p-4 transition-colors hover:border-emerald-400/40">
                <MessageCircle className="h-5 w-5 text-emerald-400" /><span className="text-[11px] font-semibold">WhatsApp</span>
              </a>
              <a href={`tel:${telPhone}`} data-testid="track-call-btn"
                className="flex flex-col items-center gap-1.5 rounded-2xl border border-white/10 bg-shipany-panel p-4 transition-colors hover:border-shipany-cyan/40">
                <Phone className="h-5 w-5 text-shipany-cyan" style={accent ? { color: accent } : undefined} /><span className="text-[11px] font-semibold">Call</span>
              </a>
              {brand?.support_email ? (
                <a href={`mailto:${brand.support_email}`} data-testid="track-support-btn" className={supportCls}>
                  <LifeBuoy className="h-5 w-5 text-shipany-blue" style={accent ? { color: accent } : undefined} /><span className="text-[11px] font-semibold">Support</span>
                </a>
              ) : (
                <Link to="/contact" data-testid="track-support-btn" className={supportCls}>
                  <LifeBuoy className="h-5 w-5 text-shipany-blue" style={accent ? { color: accent } : undefined} /><span className="text-[11px] font-semibold">Support</span>
                </Link>
              )}
            </div>

            {data.status === "DELIVERED" && (
              <div className="relative overflow-hidden rounded-2xl border border-shipany-cyan/30 bg-gradient-to-br from-shipany-blue/25 to-shipany-cyan/10 p-5 text-center" data-testid="track-ship-with-us-cta">
                <PackageCheck className="mx-auto h-8 w-8 text-shipany-cyan" style={accent ? { color: accent } : undefined} />
                <p className="font-display mt-2 text-lg font-bold">Package delivered. Enjoyed the experience?</p>
                <p className="mx-auto mt-1 max-w-xs text-xs text-slate-400">Ship your own parcels with ShipAny — compare live courier prices, print labels, and track everything. Free to start.</p>
                <Link to="/signup" data-testid="track-cta-signup-btn"
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-shipany-cyan px-6 py-2.5 text-sm font-bold text-shipany-navy transition-transform hover:scale-[1.03]">
                  Ship with us — it's free <ArrowRight className="h-4 w-4" />
                </Link>
                <p className="mt-2 text-[10px] text-slate-500">No subscription · pay per shipment</p>
              </div>
            )}

            <p className="pt-2 text-center text-[11px] text-slate-500">Tracked with ❤ by SHIPANY — Ship Smarter. Deliver Faster.</p>
          </div>
        )}
      </main>
  );

  if (embedded) {
    return (
      <MarketingLayout>
        <div data-testid="track-shipment-page">
          <MarketingHero kicker="Live Tracking" title="Track Your Shipment"
            sub="Enter your AWB or shipment number to see the real-time delivery journey — straight from courier scans, with no login required." />
          {body}
          <section className="bg-[#071B3A] py-16 sm:py-20" data-testid="track-help-section">
            <div className="mx-auto max-w-5xl px-5">
              <div className="grid gap-5 sm:grid-cols-3">
                {[
                  ["01", "Find your AWB", "Check your shipping confirmation SMS, email, package label, or the tracking link shared by the seller."],
                  ["02", "Enter it above", "Type or paste the AWB / shipment number into the tracking box and hit Track."],
                  ["03", "See live milestones", "Follow every scan from pickup through in-transit, out-for-delivery and proof of delivery."],
                ].map(([n, t, d]) => (
                  <div key={n} data-testid={`track-help-card-${n}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                    <span className="mono text-xs font-bold text-shipany-cyan">{n}</span>
                    <h3 className="font-display mt-2 text-sm font-bold text-white">{t}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
          <GradientCta title="Shipping for your business?" sub="Create a free ShipAny account to book, track and manage all your shipments from one dashboard."
            primary={["Sign Up Free", "/signup"]} secondary={["Contact Us", "/contact"]} testId="track-final-cta" />
        </div>
      </MarketingLayout>
    );
  }

  return (
    <div className="min-h-screen bg-shipany-navy text-white">
      {header}
      {body}
      {!embedded && <TrackMarketingSections />}
    </div>
  );
}

const STATUS_CARDS = [
  { icon: Package, t: "Shipment Booked" }, { icon: PackageCheck, t: "Pickup Completed" },
  { icon: Truck, t: "In Transit" }, { icon: MapPin, t: "Out for Delivery" },
  { icon: CheckCircle2, t: "Delivered" }, { icon: RotateCcw, t: "Delivery Attempted" },
  { icon: AlertTriangle, t: "NDR Raised" },
];

const DASHBOARD_POINTS = ["Shipment search", "Status filters", "Courier information", "Delivery timeline", "Bulk tracking", "Shipment history"];

function TrackMarketingSections() {
  return (
    <>
      <section className="bg-[#071B3A] py-16 sm:py-20" data-testid="track-status-cards-section">
        <div className="mx-auto max-w-6xl px-5">
          <h2 className="text-center font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">Track Every Shipment. Every Step.</h2>
          <p className="mx-auto mt-2 max-w-xl text-center text-sm text-slate-400">Every delivery milestone, normalized into one clear timeline.</p>
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
            {STATUS_CARDS.map((s, i) => (
              <motion.div key={s.t} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`track-status-card-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-center transition-colors hover:border-shipany-cyan/40">
                <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-shipany-blue/30"><s.icon className="h-4 w-4 text-shipany-cyan" /></span>
                <p className="mt-2.5 text-[11px] font-bold text-white">{s.t}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-shipany-navy py-16 sm:py-20" data-testid="track-dashboard-section">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-shipany-cyan">For businesses</span>
            <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">One Dashboard. Complete Visibility.</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">Monitor every shipment across every courier partner from one centralized dashboard — no more logging into multiple courier panels.</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {DASHBOARD_POINTS.map((p) => (
                <div key={p} className="flex items-center gap-2 text-sm text-slate-200" data-testid={`track-dash-point-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <Check className="h-4 w-4 shrink-0 text-[#10B981]" />{p}
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-3xl border border-white/10 bg-[#0B1E3D] p-6" data-testid="track-dashboard-mockup">
            <div className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4 text-shipany-cyan" />
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">ShipAny dashboard</p>
            </div>
            <div className="mt-4 space-y-2.5">
              {[["Picked Up", "text-shipany-cyan"], ["In Transit", "text-[#FF9933]"], ["Out for Delivery", "text-amber-400"], ["Delivered", "text-emerald-400"]].map(([s, c], i) => (
                <div key={s} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
                  <span className="mono text-[11px] text-slate-400">AWB ••••••{1024 + i}</span>
                  <span className={`text-[11px] font-bold ${c}`}>{s}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-center text-[10px] text-slate-600">Illustrative preview — your live shipments appear in the app</p>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#071B3A] py-16 sm:py-20" data-testid="track-customer-exp-section">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="order-2 rounded-3xl border border-white/10 bg-white/[0.04] p-6 lg:order-1" data-testid="track-branded-mockup">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#FF9933]/20 font-display text-sm font-extrabold text-[#FF9933]">B</span>
              <div>
                <p className="text-sm font-bold text-white">Your Brand</p>
                <p className="text-[10px] text-slate-500">Powered by SHIPANY</p>
              </div>
            </div>
            <div className="mt-4 h-1.5 overflow-hidden rounded bg-white/10">
              <motion.span initial={{ width: 0 }} whileInView={{ width: "70%" }} viewport={{ once: true }} transition={{ duration: 1.2 }}
                className="block h-full rounded bg-[#FF9933]" />
            </div>
            <div className="mt-4 space-y-2">
              {["Order Confirmed", "Picked Up", "In Transit"].map((s) => (
                <div key={s} className="flex items-center gap-2 text-xs text-slate-300"><CheckCircle2 className="h-3.5 w-3.5 text-[#FF9933]" />{s}</div>
              ))}
              <div className="flex items-center gap-2 text-xs text-slate-500"><Circle className="h-3.5 w-3.5" />Out for Delivery</div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="order-1 lg:order-2">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#FF9933]">White-label tracking</span>
            <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl">Give Your Customers a Better Tracking Experience</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">Keep customers informed with clear shipment status updates and a professional tracking experience — under your own brand, with your support contacts.</p>
            <Link to="/signup" data-testid="track-customer-exp-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
              Start Shipping with ShipAny <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
